import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MediaModal from "../../components/admin/MediaModal";
import { toast } from "react-toastify";
import { fetchMedias } from "../../features/admin/MediaSlice";
import ApiCustom from "../../utils/ApiCustom";
import { PencilIcon, TrashIcon, FolderIcon } from "@heroicons/react/24/outline";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");
const hasPermission = (requiredPermission) => {
  if (userId == 1) return true;
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const CATEGORY_TAB_PREFIX = "category-";
const MEDIA_CATEGORY_TYPE = "image";
const SPONSOR_CATEGORY_SLUG = "nha-tai-tro";

const slugify = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

const isSponsorCategory = (category) => {
  return category?.slug === SPONSOR_CATEGORY_SLUG;
};

const Medias = () => {
  const dispatch = useDispatch();
  const { medias, status, error } = useSelector((state) => state.medias);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [filteredMedias, setFilteredMedias] = useState([]);
  const [currentTab, setCurrentTab] = useState("image");

  const [mediaCategories, setMediaCategories] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryParentId, setCategoryParentId] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categorySaving, setCategorySaving] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [categoryError, setCategoryError] = useState("");

  const currentCategoryId = currentTab.startsWith(CATEGORY_TAB_PREFIX)
    ? currentTab.replace(CATEGORY_TAB_PREFIX, "")
    : null;

  const parentCategories = mediaCategories.filter((category) => !category.parentId);

  const currentCategory = currentCategoryId
    ? mediaCategories.find(
        (category) => Number(category.id) === Number(currentCategoryId)
      )
    : null;

  const sponsorChildren =
    currentCategory && isSponsorCategory(currentCategory)
      ? mediaCategories.filter(
          (category) => Number(category.parentId) === Number(currentCategory.id)
        )
      : [];

  const fetchMediaCategories = async () => {
    try {
      const response = await ApiCustom.get("/media-category-shows", {
        params: {
          limit: 1000,
          type: MEDIA_CATEGORY_TYPE,
        },
      });

      const data = response.data?.data?.data || [];
      setMediaCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Không tải được danh mục ảnh");
    }
  };

  useEffect(() => {
    dispatch(fetchMedias());
    fetchMediaCategories();
  }, [dispatch]);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchMedias());
    }
  }, [status, dispatch]);

  useEffect(() => {
    const filter = medias?.filter((media) => {
      if (currentTab === "image") {
        return media.imageableType === "product" && !media.mediaCategoryId;
      }

      if (currentTab === "avatar") {
        return ["user", "companie"].includes(media.imageableType);
      }

      if (currentCategoryId) {
        return Number(media.mediaCategoryId) === Number(currentCategoryId);
      }

      return false;
    });

    setFilteredMedias(filter);
  }, [medias, currentTab, currentCategoryId]);

  const handleOpenModal = (media = null) => {
    setSelectedMedia(media);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMedia(null);
    dispatch(fetchMedias());
  };

  const handleOpenCategoryModal = (category = null) => {
    setEditingCategory(category);
    setCategoryParentId(category?.parentId || null);
    setCategoryName(category?.name || "");
    setCategoryError("");
    setIsCategoryModalOpen(true);
  };

  const handleOpenChildCategoryModal = () => {
    if (!currentCategory || !isSponsorCategory(currentCategory)) {
      return;
    }

    setEditingCategory(null);
    setCategoryParentId(currentCategory.id);
    setCategoryName("");
    setCategoryError("");
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
    setCategoryParentId(null);
    setCategoryName("");
    setCategorySaving(false);
    setCategoryError("");
  };

  const handleOpenChildCategory = (category) => {
    setCurrentTab(`${CATEGORY_TAB_PREFIX}${category.id}`);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();

    const name = categoryName.trim();
    const fixedSponsor = editingCategory && isSponsorCategory(editingCategory);

    if (!name) {
      setCategoryError("Tên danh mục ảnh không được để trống");
      return;
    }

    if (fixedSponsor) {
      setCategoryError("Không được đổi tên danh mục Nhà tài trợ");
      return;
    }

    const payload = {
      name,
      slug: slugify(name),
      parentId: categoryParentId || null,
      type: MEDIA_CATEGORY_TYPE,
      status: editingCategory?.status ?? true,
    };

    try {
      setCategorySaving(true);

      if (editingCategory) {
        await ApiCustom.put(
          `/media-category-update/${editingCategory.id}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        toast.success("Cập nhật danh mục ảnh thành công");
      } else {
        await ApiCustom.post("/media-category-create", payload, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        toast.success(
          categoryParentId
            ? "Thêm thư mục con thành công"
            : "Thêm danh mục ảnh thành công"
        );
      }

      handleCloseCategoryModal();
      fetchMediaCategories();
    } catch (error) {
      toast.error(
        error.response?.data?.messages ||
          error.response?.data?.message ||
          "Lưu danh mục ảnh thất bại"
      );
    } finally {
      setCategorySaving(false);
    }
  };

  const handleConfirmDeleteCategory = async () => {
    if (!deleteCategory) return;

    if (isSponsorCategory(deleteCategory)) {
      toast.error("Không được xóa danh mục Nhà tài trợ");
      setDeleteCategory(null);
      return;
    }

    try {
      await ApiCustom.delete(`/media-category-delete/${deleteCategory.id}`);

      if (currentTab === `${CATEGORY_TAB_PREFIX}${deleteCategory.id}`) {
        setCurrentTab("image");
      }

      toast.success("Xóa danh mục ảnh thành công");
      setDeleteCategory(null);
      fetchMediaCategories();
      dispatch(fetchMedias());
    } catch (error) {
      toast.error(
        error.response?.data?.messages ||
          error.response?.data?.message ||
          "Xóa danh mục ảnh thất bại"
      );
    }
  };

  if (status === "loading") return <p>Đang tải...</p>;
  if (status === "failed") return <p>Lỗi: {error}</p>;

  return (
    <div className="p-6">
      <style>
        {`
          .media-category-tabs::-webkit-scrollbar {
            height: 4px;
          }

          .media-category-tabs::-webkit-scrollbar-track {
            background: transparent;
          }

          .media-category-tabs::-webkit-scrollbar-thumb {
            background: #f8fafc;
            border-radius: 9999px;
          }

          .media-category-tabs::-webkit-scrollbar-thumb:hover {
            background: #e5e7eb;
          }

          .media-category-tabs {
            scrollbar-width: thin;
            scrollbar-color: #f8fafc transparent;
          }
        `}
      </style>

      <h1 className="text-2xl font-bold mb-4">Quản lý hình ảnh</h1>

      {hasPermission("MEDIA_UPLOAD") && (
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-success px-4 py-2 rounded mb-4"
        >
          + Thêm hình ảnh
        </button>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <div className="-mb-px flex items-stretch gap-3">
          <nav className="media-category-tabs flex space-x-4 overflow-x-auto flex-1">
            <button
              onClick={() => setCurrentTab("image")}
              className={`whitespace-nowrap h-14 px-1 border-b-2 flex items-center flex-shrink-0 ${
                currentTab === "image"
                  ? "border-blue-500 text-blue-500"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Hình ảnh
            </button>

            <button
              onClick={() => setCurrentTab("avatar")}
              className={`whitespace-nowrap h-14 px-1 border-b-2 flex items-center flex-shrink-0 ${
                currentTab === "avatar"
                  ? "border-blue-500 text-blue-500"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Hội viên
            </button>

            {parentCategories.map((category) => {
              const tabKey = `${CATEGORY_TAB_PREFIX}${category.id}`;
              const fixedSponsor = isSponsorCategory(category);

              return (
                <div
                  key={category.id}
                  onClick={() => setCurrentTab(tabKey)}
                  className={`group whitespace-nowrap h-14 px-1 border-b-2 flex items-center gap-2 cursor-pointer flex-shrink-0 ${
                    currentTab === tabKey
                      ? "border-blue-500 text-blue-500"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span>{category.name}</span>

                  <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {hasPermission("MEDIA_UPDATE") && !fixedSponsor && (
                      <button
                        type="button"
                        title="Sửa danh mục"
                        className="w-7 h-7 flex items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenCategoryModal(category);
                        }}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}

                    {hasPermission("MEDIA_DELETE") && !fixedSponsor && (
                      <button
                        type="button"
                        title="Xóa danh mục"
                        className="w-7 h-7 flex items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteCategory(category);
                        }}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </nav>

          {hasPermission("MEDIA_CREATE") && (
            <button
              type="button"
              onClick={() => handleOpenCategoryModal()}
              className="h-12 px-2 mb-2 button btn btn-success rounded-md whitespace-nowrap flex-shrink-0 text-sm text-black font-medium"
            >
              + Thêm danh mục ảnh
            </button>
          )}
        </div>
      </div>

      {currentCategory && isSponsorCategory(currentCategory) && (
        <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Thư mục con của Nhà tài trợ
              </h2>
            </div>

            {hasPermission("MEDIA_CREATE") && (
              <button
                type="button"
                onClick={handleOpenChildCategoryModal}
                className="btn btn-primary btn-sm rounded-md"
              >
                + Tạo thư mục con
              </button>
            )}
          </div>

          {sponsorChildren.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {sponsorChildren.map((category) => (
                <div
                  key={category.id}
                  onClick={() => handleOpenChildCategory(category)}
                  className="group rounded-lg border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 transition cursor-pointer p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span className="font-medium text-gray-800 truncate">
                        {category.name}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {hasPermission("MEDIA_UPDATE") && (
                        <button
                          type="button"
                          title="Sửa thư mục con"
                          className="w-7 h-7 flex items-center justify-center rounded-md border border-blue-200 bg-white text-blue-600 hover:bg-blue-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCategoryModal(category);
                          }}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}

                      {hasPermission("MEDIA_DELETE") && (
                        <button
                          type="button"
                          title="Xóa thư mục con"
                          className="w-7 h-7 flex items-center justify-center rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteCategory(category);
                          }}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Chưa có thư mục con trong Nhà tài trợ
            </p>
          )}
        </div>
      )}

      {/* Danh sách hình ảnh */}
      {filteredMedias?.length > 0 ? (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredMedias.map((media) => (
            <div
              key={media.id}
              className="border rounded-lg overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition"
              onClick={() => handleOpenModal(media)}
            >
              <img
                src={media.url}
                alt="Media"
                className="w-full h-32 lg:h-48 object-contain"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 mt-20">
          Chưa có ảnh được tải lên
        </p>
      )}

      {/* Modal */}
      {isModalOpen && (
        <MediaModal
          imageableId={userId}
          imageableType={currentTab === "avatar" ? "user" : "product"}
          imageType={currentTab === "avatar" ? "avatar" : "thumbnail"}
          mediaCategoryId={currentCategoryId}
          media={selectedMedia}
          onClose={handleCloseModal}
          multiImage={true}
        />
      )}

      {/* Modal thêm/sửa danh mục */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-50 flex justify-center items-center">
          <div className="bg-base-100 p-6 rounded-md w-96">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold mb-4">
                {editingCategory
                  ? "Chỉnh sửa danh mục ảnh"
                  : categoryParentId
                  ? "Tạo thư mục con"
                  : "Tạo danh mục ảnh"}
              </h2>

              <button
                type="button"
                onClick={handleCloseCategoryModal}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
                disabled={categorySaving}
              >
                X
              </button>
            </div>

            <form onSubmit={handleSaveCategory}>
              {categoryParentId && !editingCategory && (
                <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-100 text-sm text-blue-700">
                  Thư mục con của:{" "}
                  <strong>
                    {
                      mediaCategories.find(
                        (category) =>
                          Number(category.id) === Number(categoryParentId)
                      )?.name
                    }
                  </strong>
                </div>
              )}

              <label className="block text-sm mb-2 font-medium">
                Tên danh mục ảnh<i className="text-red-600"> *</i>
              </label>

              <input
                type="text"
                placeholder="Nhập danh mục ảnh"
                value={categoryName}
                onChange={(e) => {
                  setCategoryError("");
                  setCategoryName(e.target.value);
                }}
                className="input input-bordered w-full mb-4"
                disabled={categorySaving}
              />

              {categoryError && (
                <p className="text-red-500 text-sm mb-4">{categoryError}</p>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleCloseCategoryModal}
                  disabled={categorySaving}
                >
                  Hủy
                </button>

                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={categorySaving}
                >
                  {categorySaving
                    ? "Đang lưu..."
                    : editingCategory
                    ? "Cập nhật"
                    : "Tạo"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal xác nhận xóa danh mục */}
      {deleteCategory && (
        <div className="fixed inset-0 z-30 bg-gray-500 bg-opacity-50 flex justify-center items-center">
          <div className="bg-base-100 p-6 rounded-md w-96">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold mb-4">Xóa danh mục ảnh</h2>

              <button
                type="button"
                onClick={() => setDeleteCategory(null)}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
              >
                X
              </button>
            </div>

            <p className="mb-6">
              Bạn có chắc muốn xóa danh mục ảnh{" "}
              <strong>{deleteCategory.name}</strong> không?
            </p>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setDeleteCategory(null)}
              >
                Hủy
              </button>

              <button
                type="button"
                className="btn btn-error btn-sm"
                onClick={handleConfirmDeleteCategory}
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Medias;